import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'

import { ReleaseDto } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { BranchBadge, BranchSection } from '@/pages/User/pages/Branches/components/BranchDetailUi'
import { AddEnvironment } from '@/pages/User/pages/Home/pages/SystemDetail/pages/DeploymentPackages/components/Environments/AddEnvironment'
import { DdlModal } from '@/pages/User/pages/Home/pages/SystemDetail/pages/DeploymentPackages/components/Environments/DdlModal'
import { EnvironmentEditModal } from '@/pages/User/pages/Home/pages/SystemDetail/pages/DeploymentPackages/components/Environments/EnvironmentEditModal'
import { useDeleteReleaseEnvironmentMutation } from '@/rtkApi'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { ReleaseEnvironmentDto } from '@/rtkApi/mddApiSliceGeneratedV2'
import { DdlModalType } from '@/types'
import { getApiErrorDescription, logErrorAndNotifyWithToast } from '@/utils/errorHandling'

import { ConfirmDialog } from './ConfirmDialog'
import { EmptyRow, RowCard } from './rowUi'

type EnvDialog =
	| { env: ReleaseEnvironmentDto; kind: 'create' | 'delete' | 'edit' | 'push' }
	| { kind: 'add' }

type PackageEnvironmentsProps = {
	canEdit: boolean
	environments: ReleaseEnvironmentDto[]
	legacyPackage: ReleaseDto
	onChanged: () => void
	systemNode: StructureDto
}

export const PackageEnvironments = ({
	canEdit,
	environments,
	legacyPackage,
	onChanged,
	systemNode,
}: PackageEnvironmentsProps) => {
	const { t } = useAppContext()
	const [dialog, setDialog] = useState<EnvDialog | null>(null)
	const [deleteEnvironment] = useDeleteReleaseEnvironmentMutation()

	const close = (changed = false) => {
		setDialog(null)
		if (changed) onChanged()
	}

	const removeEnvironment = async (env: ReleaseEnvironmentDto) => {
		close()
		try {
			await deleteEnvironment({ dpId: legacyPackage.id!, envCode: env.code ?? '' }).unwrap()
			onChanged()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'PackageEnvironments.removeEnvironment' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'DEPLOYMENT_PACKAGES_UPDATE_FAILED',
				t,
			})
		}
	}

	return (
		<BranchSection title={t('ENVIRONMENTS_IN_RELEASE')}>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, mt: -4.5 }}>
				<Button
					size="small"
					variant="outlined"
					startIcon={<AddOutlinedIcon sx={{ fontSize: 14 }} />}
					disabled={!canEdit}
					onClick={() => setDialog({ kind: 'add' })}
					data-testid="deployment-package-add-environment"
					sx={{ borderRadius: '8px', height: 30, textTransform: 'none' }}
				>
					{t('ADD_ENVIRONMENT')}
				</Button>
			</Box>

			{environments.length === 0 ? (
				<EmptyRow message={t('DEPLOYMENT_PACKAGES_NO_ENVIRONMENTS')} />
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					{environments.map((env) => (
						<RowCard key={env.code ?? env.id} testId={`deployment-package-env-${env.code}`}>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
									<Typography sx={{ color: '#2A2A2A', fontSize: 13, fontWeight: 600 }}>
										{env.name || env.code}
									</Typography>
									<BranchBadge color="#52666C" background="#EEF1F2">
										{env.code}
									</BranchBadge>
									{env.released ? (
										<BranchBadge color="#0F9D58" background="#E6F4EA">
											{t('RELEASED')}
										</BranchBadge>
									) : null}
								</Box>
								<Typography sx={{ color: '#9A9A9A', fontSize: 11.5, mt: 0.25 }}>
									{env.releaseDate
										? `${t('RELEASE_DATE')}: ${env.releaseDate}`
										: t('DEPLOYMENT_PACKAGES_NO_RELEASE_DATE')}
								</Typography>
							</Box>
							<Button
								size="small"
								variant="outlined"
								onClick={() => setDialog({ env, kind: 'push' })}
								sx={{ borderRadius: '8px', height: 30, textTransform: 'none' }}
							>
								{t('PUSH_TO_GIT')}
							</Button>
							<Button
								size="small"
								variant="contained"
								disableElevation
								onClick={() => setDialog({ env, kind: 'create' })}
								sx={{ borderRadius: '8px', height: 30, textTransform: 'none' }}
							>
								{t('DEPLOYMENT_PACKAGES_CREATE_SCRIPTS')}
							</Button>
							<Tooltip title={t('EDIT')} arrow>
								<span>
									<IconButton
										size="small"
										disabled={!canEdit}
										onClick={() => setDialog({ env, kind: 'edit' })}
									>
										<EditOutlinedIcon sx={{ fontSize: 17 }} />
									</IconButton>
								</span>
							</Tooltip>
							<Tooltip title={t('REMOVE')} arrow>
								<span>
									<IconButton
										size="small"
										disabled={!canEdit}
										onClick={() => setDialog({ env, kind: 'delete' })}
										sx={{ '&:hover': { color: '#C62828' } }}
									>
										<DeleteOutlineOutlinedIcon sx={{ fontSize: 17 }} />
									</IconButton>
								</span>
							</Tooltip>
						</RowCard>
					))}
				</Box>
			)}

			{dialog?.kind === 'add' ? (
				<AddEnvironment
					node={systemNode}
					deploymentPackage={legacyPackage}
					envs={environments}
					onClose={() => close(true)}
				/>
			) : null}
			{dialog?.kind === 'edit' ? (
				<EnvironmentEditModal
					env={dialog.env}
					node={systemNode}
					release={legacyPackage}
					onClose={() => close(true)}
				/>
			) : null}
			{dialog?.kind === 'push' ? (
				<DdlModal
					preview
					env={dialog.env}
					node={systemNode}
					deploymentPackage={legacyPackage}
					type={DdlModalType.PushToGit}
					onClose={() => close()}
				/>
			) : null}
			{dialog?.kind === 'create' ? (
				<DdlModal
					env={dialog.env}
					node={systemNode}
					deploymentPackage={legacyPackage}
					type={DdlModalType.CreateGit}
					onClose={() => close(true)}
				/>
			) : null}
			<ConfirmDialog
				open={dialog?.kind === 'delete'}
				danger
				title={t('REMOVE')}
				message={t('DEPLOYMENT_PACKAGES_REMOVE_ENVIRONMENT', [
					dialog?.kind === 'delete' ? (dialog.env.name ?? dialog.env.code ?? '') : '',
				])}
				confirmLabel={t('REMOVE')}
				onClose={() => close()}
				onConfirm={() => {
					if (dialog?.kind === 'delete') void removeEnvironment(dialog.env)
				}}
			/>
		</BranchSection>
	)
}
